import { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  MenuItem,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import type { IdentityVerification } from '../../app/types';
import { fileToBase64 } from '../../lib/files';
import PageSection from '../shared/PageSection';

interface VerifyIdentityPageProps {
  readonly tenantId: string;
  readonly verifications: IdentityVerification[];
  readonly onVerify: (payload: {
    tenantId: string;
    idNumber: string;
    sourceImageName: string;
    imageBase64: string;
  }) => Promise<IdentityVerification>;
  readonly onReview: (
    verificationId: string,
    payload: { tenantId: string; reviewStatus: 'approved' | 'rejected'; reviewNotes?: string }
  ) => Promise<void>;
  readonly onGetProbePhotoUrl: (verificationId: string, tenantId: string) => Promise<string>;
}

const PAGE_SIZE = 6;
const MAX_IMAGE_FILE_BYTES = 5 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = new Set(['image/jpeg', 'image/png']);

export default function VerifyIdentityPage({
  tenantId,
  verifications,
  onVerify,
  onReview,
  onGetProbePhotoUrl,
}: VerifyIdentityPageProps) {
  const [idNumber, setIdNumber] = useState('');
  const [historyIdNumber, setHistoryIdNumber] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [reviewFilter, setReviewFilter] = useState('all');
  const [reviewNotes, setReviewNotes] = useState('');
  const [page, setPage] = useState(1);
  const [result, setResult] = useState<IdentityVerification | null>(null);
  const [error, setError] = useState('');
  const [running, setRunning] = useState(false);
  const [reviewingId, setReviewingId] = useState('');
  const [previewVerificationId, setPreviewVerificationId] = useState('');
  const [previewImageUrl, setPreviewImageUrl] = useState('');

  const filteredVerifications = useMemo(() => {
    const normalizedIdNumber = historyIdNumber.trim().toUpperCase();
    return verifications.filter((verification) => {
      if (statusFilter !== 'all' && verification.status !== statusFilter) {
        return false;
      }
      if (reviewFilter !== 'all' && verification.reviewStatus !== reviewFilter) {
        return false;
      }
      if (normalizedIdNumber && !verification.idNumber.toUpperCase().includes(normalizedIdNumber)) {
        return false;
      }
      return true;
    });
  }, [historyIdNumber, reviewFilter, statusFilter, verifications]);

  const pageCount = Math.max(1, Math.ceil(filteredVerifications.length / PAGE_SIZE));
  const visibleVerifications = filteredVerifications.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const runVerification = async (files: FileList | null) => {
    const file = files?.[0];
    if (!file) {
      return;
    }
    if (file.size > MAX_IMAGE_FILE_BYTES) {
      setError('Probe image is too large. Maximum supported size is 5 MB.');
      return;
    }
    if (!ALLOWED_IMAGE_TYPES.has(file.type)) {
      setError('Unsupported image format. Please upload a JPEG or PNG image.');
      return;
    }

    try {
      setRunning(true);
      setError('');
      const imageBase64 = await fileToBase64(file);
      const verification = await onVerify({
        tenantId,
        idNumber,
        sourceImageName: file.name,
        imageBase64,
      });
      setResult(verification);
      setReviewNotes('');
    } catch (verifyError) {
      setError(verifyError instanceof Error ? verifyError.message : 'Unable to verify identity.');
      setResult(null);
    } finally {
      setRunning(false);
    }
  };

  const reviewVerification = async (verificationId: string, reviewStatus: 'approved' | 'rejected') => {
    try {
      setReviewingId(verificationId);
      setError('');
      await onReview(verificationId, {
        tenantId,
        reviewStatus,
        reviewNotes,
      });
      setReviewNotes('');
    } catch (reviewError) {
      setError(reviewError instanceof Error ? reviewError.message : 'Unable to review verification.');
    } finally {
      setReviewingId('');
    }
  };

  const openProbePreview = async (verificationId: string) => {
    try {
      setError('');
      setPreviewVerificationId(verificationId);
      const url = await onGetProbePhotoUrl(verificationId, tenantId);
      setPreviewImageUrl(url);
    } catch (previewError) {
      setPreviewVerificationId('');
      setPreviewImageUrl('');
      setError(previewError instanceof Error ? previewError.message : 'Unable to load probe image preview.');
    }
  };

  const closeProbePreview = () => {
    setPreviewVerificationId('');
    setPreviewImageUrl('');
  };

  return (
    <>
      <Stack spacing={3}>
        <PageSection title="Verify Identity" subtitle="Upload a probe image and compare it against the reference photo stored for the entered ID number.">
          <Stack spacing={2}>
              {error ? <Alert severity="error">{error}</Alert> : null}
              <TextField label="ID Number" value={idNumber} onChange={(event) => setIdNumber(event.target.value)} />
              <Button component="label" variant="contained" disabled={!idNumber || running}>
                {running ? 'Verifying...' : 'Upload Probe Photo'}
                <input
                  hidden
                  type="file"
                  accept="image/jpeg,image/png"
                  onChange={(event) => void runVerification(event.target.files)}
                />
              </Button>
          </Stack>
        </PageSection>

        <PageSection title="Latest Result" subtitle="Most recent verification run from this console session.">
          {result ? (
            <Stack spacing={1.5}>
                <Typography><strong>Verification ID:</strong> {result.verificationId}</Typography>
                <Typography><strong>ID Number:</strong> {maskIdNumber(result.idNumber)}</Typography>
                <Typography><strong>Status:</strong> {result.status}</Typography>
                <Typography><strong>Matched:</strong> {result.matched ? 'Yes' : 'No'}</Typography>
                <Typography><strong>Confidence:</strong> {result.confidence.toFixed(1)}%</Typography>
                <Typography><strong>Threshold:</strong> {result.threshold}%</Typography>
                <Typography><strong>Review Status:</strong> {result.reviewStatus}</Typography>
                <Typography><strong>Failure Reason:</strong> {result.failureReason ?? 'None'}</Typography>
                <Typography><strong>Face Results:</strong> {result.results.length}</Typography>
                {result.results.map((faceResult) => (
                  <Typography key={faceResult.faceIndex} variant="body2" color="text.secondary">
                    Face {faceResult.faceIndex + 1}: {faceResult.status} ({faceResult.confidence.toFixed(1)}%)
                  </Typography>
                ))}
                <TextField
                  label="Review Notes"
                  value={reviewNotes}
                  onChange={(event) => setReviewNotes(event.target.value)}
                  multiline
                  minRows={2}
                />
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained"
                    color="success"
                    disabled={reviewingId === result.verificationId}
                    onClick={() => void reviewVerification(result.verificationId, 'approved')}
                  >
                    Approve
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    disabled={reviewingId === result.verificationId}
                    onClick={() => void reviewVerification(result.verificationId, 'rejected')}
                  >
                    Reject
                  </Button>
                </Stack>
            </Stack>
          ) : (
            <Typography color="text.secondary">No verification has been run from this page yet.</Typography>
          )}
        </PageSection>

        <PageSection title="Verification History" subtitle="Historical 1:1 identity checks for the active tenant scope.">
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="ID Number"
                value={historyIdNumber}
                onChange={(event) => {
                  setHistoryIdNumber(event.target.value);
                  setPage(1);
                }}
              />
              <TextField
                select
                label="Status"
                value={statusFilter}
                onChange={(event) => {
                  setStatusFilter(event.target.value);
                  setPage(1);
                }}
                sx={{ minWidth: 180 }}
              >
                <MenuItem value="all">All statuses</MenuItem>
                <MenuItem value="matched">Matched</MenuItem>
                <MenuItem value="not-matched">Not Matched</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
              </TextField>
              <TextField
                select
                label="Review"
                value={reviewFilter}
                onChange={(event) => {
                  setReviewFilter(event.target.value);
                  setPage(1);
                }}
                sx={{ minWidth: 180 }}
              >
                <MenuItem value="all">All review states</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </TextField>
            </Stack>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Created</TableCell>
                  <TableCell>ID Number</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Review</TableCell>
                  <TableCell>Confidence</TableCell>
                  <TableCell>Threshold</TableCell>
                  <TableCell>Failure</TableCell>
                  <TableCell>Probe Photo</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleVerifications.map((verification) => (
                  <TableRow key={verification.verificationId}>
                    <TableCell>{new Date(verification.createdAt).toLocaleString()}</TableCell>
                    <TableCell>{maskIdNumber(verification.idNumber)}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={verification.status}
                        color={verification.matched ? 'success' : verification.status === 'failed' ? 'warning' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.5}>
                        <Chip
                          size="small"
                          label={verification.reviewStatus}
                          color={
                            verification.reviewStatus === 'approved'
                              ? 'success'
                              : verification.reviewStatus === 'rejected'
                                ? 'error'
                                : 'default'
                          }
                        />
                        <Typography variant="caption" color="text.secondary">
                          {verification.reviewedAt ? new Date(verification.reviewedAt).toLocaleString() : 'Pending review'}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>{verification.confidence.toFixed(1)}%</TableCell>
                    <TableCell>{verification.threshold}%</TableCell>
                    <TableCell>{verification.failureReason ?? 'None'}</TableCell>
                    <TableCell>
                      {verification.probeImageKey && !verification.probeImagePurgedAt ? (
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => void openProbePreview(verification.verificationId)}
                          disabled={previewVerificationId === verification.verificationId && !previewImageUrl}
                        >
                          {previewVerificationId === verification.verificationId && !previewImageUrl ? 'Loading...' : 'View Photo'}
                        </Button>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          Not available
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {!visibleVerifications.length ? (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Typography color="text.secondary">No verifications match the current filters.</Typography>
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>

            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="caption" color="text.secondary">
                Showing {visibleVerifications.length} of {filteredVerifications.length} verifications
              </Typography>
              <Pagination page={page} count={pageCount} onChange={(_, value) => setPage(value)} size="small" />
            </Stack>
          </Stack>
        </PageSection>
      </Stack>

      <Dialog open={Boolean(previewVerificationId)} onClose={closeProbePreview} maxWidth="md" fullWidth>
        <DialogTitle>Probe Photo</DialogTitle>
        <DialogContent>
          {previewImageUrl ? (
            <img
              src={previewImageUrl}
              alt="Verification probe"
              style={{ width: '100%', height: 'auto', borderRadius: 8, display: 'block' }}
            />
          ) : (
            <Typography color="text.secondary">Loading image...</Typography>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function maskIdNumber(value: string): string {
  if (value.length <= 4) {
    return value;
  }

  return `${'*'.repeat(Math.max(0, value.length - 4))}${value.slice(-4)}`;
}
